// run-command-cli-wiring.ts
//
// `avatar autopilot run`'s full action body (P9b) — split out of
// commands/autopilot.ts purely to keep that file ≤200 lines (same reason
// autopilot-control-directive-commands.ts / autopilot-ops-checks-cli-wiring.ts
// were split in earlier phases). This is what closes the gap found at P8
// review: `run` used to stop after `startSupervisor`'s one-time gate +
// bootstrap — the real dispatch loop (`runMissionToIdle` ->
// `runSupervisorUntilIdle`) had no production caller. Now `run` calls
// `runMissionToIdle` directly (it internally calls `startSupervisor` itself
// and always releases the machine lock — callers must NOT call
// `startSupervisor` separately, or the lock would be acquired twice).
import { join } from "node:path";
import { BOOTSTRAP_MANIFEST_PATHS } from "./bootstrap-manifest-paths.js";
import type { EffectiveConfigLoadOk } from "./effective-config-loader.js";
import { realLivenessProbe } from "./process-liveness-probe.js";
import type { RunAutopilotRunCommandInput } from "./run-command-cli-wiring-types.js";
import { applyInterruptedOutcomeOverride } from "./run-command-interrupted-outcome-override.js";
import { printRunOutcome } from "./run-command-outcome-observability.js";
import { makeRunCommandPromptBuilder } from "./run-command-prompt-builder.js";
import { buildReleaseFlowResumeInputFactory } from "./run-command-release-flow-wiring.js";
import { buildRunCommandSessionDeps, claudeBinOverrideNotice } from "./run-command-session-deps.js";
import { buildRunSignalCleanup, type RunSignalCleanup } from "./run-command-signal-cleanup.js";
import { buildRunCommandTransportFactory } from "./run-command-transport-factory.js";
import {
  installRunInterruptHandlers,
  type RunInterruptHandlers,
} from "./run-interrupt-signal-handler.js";
import { DEFAULT_SHUTDOWN_GRACE_SEC } from "./runtime-limits-from-effective-config.js";
import type { RunMissionResult } from "./supervisor-mission-run.js";

export type { RunAutopilotRunCommandInput } from "./run-command-cli-wiring-types.js";

/**
 * Runs `avatar autopilot run`'s dispatch-to-idle sequence and returns the
 * outcome. Never calls `process.exit` itself — the caller (commands/
 * autopilot.ts) decides exit codes, keeping this function unit-testable.
 */
export async function runAutopilotRunCommand(
  input: RunAutopilotRunCommandInput,
): Promise<RunMissionResult> {
  const missionWorkspace = join(input.missionHome, "workspace");
  const packRoot = join(missionWorkspace, ".claude", "pack");

  const notice = claudeBinOverrideNotice();
  if (notice) input.log.info(notice);

  const supervisorStartedAt = await realLivenessProbe.processStartedAt(process.pid);
  const { sessionDeps, currentWorker } = buildRunCommandSessionDeps({
    controlRoot: input.controlRoot,
    supervisorStartedAt,
  });
  let graceMs = DEFAULT_SHUTDOWN_GRACE_SEC * 1000;
  let release: EffectiveConfigLoadOk["config"]["release"];
  // 06g R2c: lock/lease on force exit; binding + active-worker after reap.
  const cleanup = buildRunSignalCleanup({
    machineLockPath: input.machineLockPath,
    controlRoot: input.controlRoot,
    missionWorkspace,
  });
  const interrupt = installRunInterruptHandlers({
    currentWorker,
    currentGraceMs: () => graceMs,
    ...cleanup,
    notify: input.log.error,
    ignoreHangup: input.ignoreHangup,
  });

  try {
    const result = await applyInterruptedOutcomeOverride({
      controlRoot: input.controlRoot,
      isInterrupted: interrupt.isInterrupted,
      log: input.log,
      run: () =>
        runOnce(input, missionWorkspace, packRoot, sessionDeps, interrupt, cleanup, (loaded) => {
          graceMs = (loaded.config.shutdown_grace_sec ?? DEFAULT_SHUTDOWN_GRACE_SEC) * 1000;
          release = loaded.config.release;
          // Req 3 (D7, C4): the ONLY place `run` learns which §18 keys the
          // user's file declared that v1 never reads — same stream/channel
          // (stdout via log.warn) the banner/header below already use, so
          // an operator sees one ordered log, never a second output mode.
          for (const w of loaded.unusedKeyWarnings) input.log.warn(w);
        }),
    });
    // BUG-1 (dogfood 260925 luồng 3): print the FINAL outcome (after the
    // interrupt override), never runOnce's raw result — printing inside runOnce
    // showed stale outcomes ("idle") while the journal said "interrupted".
    await printRunOutcome({
      missionId: input.missionId,
      controlRoot: input.controlRoot,
      result,
      log: input.log,
      missionWorkspace,
      globalConfigPath: input.globalConfigPath,
      release,
      releaseSecretsStdin: input.releaseSecretsStdin,
    });
    return result;
  } finally {
    cleanup.markRunSettled();
    // CXR-R1: an interrupt's recipe/worker stop finishes before the handler goes.
    await interrupt.drain();
    interrupt.dispose();
  }
}

type RunOnceSessionDeps = ReturnType<typeof buildRunCommandSessionDeps>["sessionDeps"];

async function runOnce(
  input: RunAutopilotRunCommandInput,
  missionWorkspace: string,
  packRoot: string,
  sessionDeps: RunOnceSessionDeps,
  interrupt: Pick<RunInterruptHandlers, "isInterrupted" | "drain">,
  cleanup: Pick<RunSignalCleanup, "markRunSettled">,
  onEffectiveConfigLoaded: (loaded: EffectiveConfigLoadOk) => void,
): Promise<RunMissionResult> {
  const { runMissionToIdle } = await import("./supervisor-mission-run.js");

  const result = await runMissionToIdle({
    missionId: input.missionId,
    workspaceRoot: input.workspaceRoot,
    missionWorkspace,
    sourceRepo: input.repoFlag ?? input.workspaceRoot,
    controlRoot: input.controlRoot,
    machineLockPath: input.machineLockPath,
    globalConfigPath: input.globalConfigPath,
    onMachineLockAcquired: input.onMachineLockAcquired,
    sessionDeps,
    isInterrupted: interrupt.isInterrupted,
    // CXR-R1: the machine lock is released only after that stop.
    // 06g R2c: the loop has returned here (post-route archive + reset done) —
    // let a SIGHUP re-raise proceed, then wait for the worker stop as before.
    beforeLockRelease: async () => {
      cleanup.markRunSettled();
      await interrupt.drain();
    },
    onEffectiveConfigLoaded,
    // (code-review phase-03 [Medium#1]) business outcomes (approve
    // rejected, abort-confirm hints) — not runtime crashes — so `warn`, not
    // `error`, to keep console output distinguishable from a real crash log
    // line during incident debugging.
    notify: (line) => input.log.warn(line),
    // Deferred: reading pack playbook files is real I/O that must NOT run
    // before the §18 beforeDispatch gate has had a chance to block a `run`
    // outright (a mission whose pack has no workflows/autopilot/ yet, e.g.
    // quiet-hours-blocked before any ticket ever dispatches, must exit on
    // the gate's reason — not on a missing-playbook contract_error that was
    // never going to matter). runMissionToIdle only calls this AFTER the
    // startup gate passes and dispatch is actually about to begin.
    buildPromptFactory: () =>
      makeRunCommandPromptBuilder(packRoot, missionWorkspace, input.controlRoot),
    // Phase 03 (R-15, B19): operator-facing progress (header/▶/■) — stdout via
    // `log.info` even in --non-interactive (D14; the JSON machine contract
    // rides stderr on error, this is a separate line never parsed as JSON).
    progress: (line) => input.log.info(line),
    // 06g R2a (H3/F-34): the explicit list of what `autopilot init` leaves
    // uncommitted — never "." (which swept a killed worker's files into
    // `[T-0000]`); anything else left in the worktree stops `run` as
    // `worktree_dirty` (supervisor-mission-run.ts).
    bootstrapManifestPaths: [...BOOTSTRAP_MANIFEST_PATHS],
    maxTicks: input.maxTicks,
    // Bug G fix: the release-flow factory takes `config` (only known inside
    // runMissionToIdle) and builds real deps lazily; no-op without config.release.
    // P19c ("0 caller" #10): same deferred-factory shape as the release flow
    // (credentials/tunables/approver live in effective-config, known only inside
    // runMissionToIdle); undefined => local-only. Req 2 (phase 05, C4): the
    // banner via `onBanner` (stdout) is the ONLY place production learns whether
    // Mattermost is wired for THIS run — printed before the loop's header line.
    mattermostTransportFactory: buildRunCommandTransportFactory({
      workspaceRoot: input.workspaceRoot,
      controlRoot: input.controlRoot,
      missionId: input.missionId,
      onBanner: input.log.info,
    }),
    releaseFlowResumeInputFactory: (config) =>
      buildReleaseFlowResumeInputFactory({
        repoDir: missionWorkspace,
        controlRoot: input.controlRoot,
        missionId: input.missionId,
        config,
        releaseSecretsStdin: input.releaseSecretsStdin,
      })(),
  });

  return result;
}
